import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  BarChart3,
  Brain,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ExternalLink,
  Flame,
  Heart,
  Mail,
  MapPin,
  Phone,
  Plus,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Users,
} from 'lucide-react-native';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInputProps,
} from 'react-native';
import { Badge, Box, FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { Avatar } from '../../../components/primitives/Avatar';
import { BentoSurface } from '../../../components/primitives/BentoSurface';
import { Button } from '../../../components/primitives/Button';
import { Select } from '../../../components/primitives/Select';
import { Sheet } from '../../../components/primitives/Sheet';
import { useModal } from '../../../contexts/ModalContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  useAddContact,
  useRemoveContact,
  useRescanContact,
  useSetPrimaryContact,
  useUpdateContact,
} from '../../../hooks/useLeads';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { CONTACT_ROLE_OPTIONS, type Contact } from '../../../types/leads';
import { enrichmentMethodLabelKey } from '../utils/enrichmentMethodLabel';
import { ProfileDivider, ProfileInfoRow } from './ProfileInfoRow';

interface ContactCarouselCardProps {
  leadId: string;
  contacts: Contact[];
}

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  role: string;
}

const EMPTY_FORM: ContactFormData = { name: '', email: '', phone: '', jobTitle: '', role: '' };

/**
 * Maps an edited text field to its PATCH value. An emptied field must travel as an
 * explicit `null`: mapping it to `undefined` drops the key from the JSON body, so the
 * backend reads it as "no change" and keeps the old value (FLOSEAI-565).
 */
function clearableField(value: string): string | null {
  return value.trim() || null;
}

function getScoreColor(score: number): string {
  if (score >= 7) return colors.status.success.main;
  if (score >= 4) return colors.status.warning.main;
  return colors.status.error.main;
}

function ScoreGauge({ score }: Readonly<{ score: number }>) {
  const { t } = useTranslation();
  const percentage = Math.round((score / 10) * 100);
  const scoreColor = getScoreColor(score);
  const bg =
    score >= 7
      ? colors.status.success.bg
      : score >= 4
        ? colors.status.warning.bg
        : colors.status.error.bg;
  const label =
    score >= 7
      ? t('leads.enrichment.highPotential')
      : score >= 4
        ? t('leads.enrichment.mediumPotential')
        : t('leads.enrichment.lowPotential');

  return (
    <Box backgroundColor={bg} borderRadius="lg" padding="md" alignItems="center">
      <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '600' }}>
        {t('leads.enrichment.potentialLabel')}
      </Text>
      <Text variant="h3" color={scoreColor} style={{ fontWeight: '800' }}>
        {score}/10
      </Text>
      <Text variant="caption" color={scoreColor} style={{ fontWeight: '600', marginTop: 2 }}>
        {label}
      </Text>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 4, marginTop: 8 }}
      >
        <Box
          backgroundColor={scoreColor}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 4 }}
        />
      </Box>
    </Box>
  );
}

function ScoreBar({
  label,
  score,
  icon,
}: Readonly<{ label: string; score: number; icon: ReactNode }>) {
  const percentage = Math.round((score / 10) * 100);
  return (
    <VStack spacing="xxs">
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="xs" align="center" style={{ flex: 1 }}>
          {icon}
          <Text variant="caption" color={colors.text.secondary} numberOfLines={1}>
            {label}
          </Text>
        </HStack>
        <Text variant="caption" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {score}/10
        </Text>
      </HStack>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 3 }}
      >
        <Box
          backgroundColor={getScoreColor(score)}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 3 }}
        />
      </Box>
    </VStack>
  );
}

function FormField({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = 'default',
}: {
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  placeholder: string;
  keyboardType?: TextInputProps['keyboardType'];
}) {
  return (
    <VStack spacing="xxs">
      <Text
        variant="caption"
        color={colors.text.secondary}
        style={{ fontWeight: '500', letterSpacing: 0.3 }}
      >
        {label}
      </Text>
      <FormInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        keyboardType={keyboardType}
        autoCapitalize={keyboardType === 'email-address' ? 'none' : 'words'}
        autoCorrect={false}
        style={styles.formInput}
      />
    </VStack>
  );
}

function ContactSlide({
  contact,
  contacts,
  onEdit,
  onSetPrimary,
  onRemove,
  onRescan,
  isRescanning,
}: Readonly<{
  contact: Contact;
  contacts: Contact[];
  onEdit: () => void;
  onSetPrimary: () => void;
  onRemove: () => void;
  onRescan: () => void;
  isRescanning: boolean;
}>) {
  const { t, language } = useTranslation();
  const cf = (contact.customFields || {}) as Record<string, unknown>;
  const score = typeof cf.score === 'number' ? cf.score : undefined;
  const followerCount = typeof cf.followerCount === 'number' ? cf.followerCount : undefined;
  const reasonScore = cf.reasonScore as Record<string, number> | undefined;
  const painPoints = Array.isArray(cf.painPoints) ? (cf.painPoints as string[]) : undefined;
  const aiAnalysis = typeof cf.aiAnalysis === 'string' ? cf.aiAnalysis : undefined;
  const economicCapacity = cf.economicCapacity as 'High' | 'Medium' | 'Low' | undefined;
  const industry = Array.isArray(cf.industry) ? (cf.industry as string[]) : undefined;
  const enrichedAt = typeof cf.enrichedAt === 'string' ? cf.enrichedAt : undefined;
  const enrichmentMethod =
    typeof cf.enrichmentMethod === 'string' ? cf.enrichmentMethod : undefined;
  const hasLinkedInBars =
    reasonScore?.painPoints != null ||
    reasonScore?.economicCapacity != null ||
    reasonScore?.investmentMentality != null ||
    reasonScore?.urgency != null;
  const hasBrandBars =
    reasonScore?.brandFocus != null ||
    reasonScore?.contentQuality != null ||
    reasonScore?.digitalPresence != null ||
    reasonScore?.marketPosition != null;
  const hasInterestBars =
    reasonScore?.contentAffinity != null ||
    reasonScore?.postingActivity != null ||
    reasonScore?.audienceReach != null ||
    reasonScore?.audienceInteraction != null;
  const hasScoreBreakdown = hasLinkedInBars || hasBrandBars || hasInterestBars;

  const {
    aiAnalysis: translatedAi,
    painPoints: translatedPain,
    isTranslating,
  } = useTranslateOnFly({
    aiAnalysis,
    painPoints,
  });

  return (
    <VStack spacing="md">
      {/* Header */}
      <Box style={{ alignItems: 'center' }}>
        <Avatar size="xl" name={contact.name || contact.email || '?'} />
        <Box height={tokens.spacing.xs} />
        <Text
          variant="h5"
          color={colors.text.primary}
          style={{ fontWeight: '700', textAlign: 'center' }}
        >
          {contact.name || contact.email || '\u2014'}
        </Text>
        {contact.headline && (
          <Text
            variant="bodySmall"
            color={colors.text.secondary}
            style={{ textAlign: 'center', marginTop: 2 }}
          >
            {contact.headline}
          </Text>
        )}
        {contact.isPrimary && (
          <HStack spacing="xs" align="center" style={{ marginTop: tokens.spacing.xs }}>
            <Star size={12} color={colors.status.warning.main} strokeWidth={2} />
            <Text
              variant="caption"
              color={colors.status.warning.main}
              style={{ fontWeight: '600' }}
            >
              {t('leads.contacts.primary')}
            </Text>
          </HStack>
        )}
        {contact.role && (
          <Badge variant="subtle" style={{ marginTop: tokens.spacing.xs }}>
            {t(`leads.contacts.roles.${contact.role}`)}
          </Badge>
        )}
      </Box>

      {/* Score */}
      {score != null && <ScoreGauge score={score} />}

      {/* Profile Info */}
      <VStack spacing="sm">
        {contact.email && (
          <ProfileInfoRow
            icon={<Mail size={14} color={colors.primary[400]} strokeWidth={1.5} />}
            label={t('leads.contacts.emailLabel')}
            value={contact.email}
          />
        )}
        {contact.phone && (
          <ProfileInfoRow
            icon={<Phone size={14} color={colors.accent.blue[400]} strokeWidth={1.5} />}
            label={t('leads.contacts.phoneLabel')}
            value={contact.phone}
          />
        )}
        {contact.jobTitle && (
          <ProfileInfoRow
            icon={<Briefcase size={14} color={colors.accent.amber[400]} strokeWidth={1.5} />}
            label={t('leads.contacts.jobTitleLabel')}
            value={contact.jobTitle}
          />
        )}
        {contact.role && (
          <ProfileInfoRow
            icon={<Shield size={14} color={colors.primary[400]} strokeWidth={1.5} />}
            label={t('leads.contacts.roleLabel')}
            value={t(`leads.contacts.roles.${contact.role}`)}
          />
        )}
        {contact.location && (
          <ProfileInfoRow
            icon={<MapPin size={14} color={colors.accent.amber[400]} strokeWidth={1.5} />}
            label={t('leads.enrichment.location')}
            value={contact.location}
          />
        )}
        {followerCount != null && (
          <ProfileInfoRow
            icon={<Users size={14} color={colors.primary[400]} strokeWidth={1.5} />}
            label={t('leads.enrichment.followers')}
            value={followerCount.toLocaleString()}
          />
        )}
        {contact.sourceUrl && (
          <ProfileInfoRow
            icon={<ExternalLink size={14} color={colors.text.tertiary} strokeWidth={1.5} />}
            label={t('leads.contacts.profileLabel')}
            value={contact.sourceUrl}
          />
        )}
        {industry && industry.length > 0 && (
          <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
            {industry.map((ind) => (
              <Badge key={ind} variant="subtle" color="blue" size="sm">
                {ind}
              </Badge>
            ))}
          </HStack>
        )}
      </VStack>

      {/* Economic Capacity */}
      {economicCapacity && (
        <>
          <ProfileDivider />
          <HStack spacing="sm" align="center" justify="between">
            <HStack spacing="xs" align="center">
              <DollarSign size={14} color={colors.accent.amber[400]} strokeWidth={2} />
              <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.enrichment.economicCapacity')}
              </Text>
            </HStack>
            <Badge
              variant="subtle"
              color={
                economicCapacity === 'High'
                  ? 'green'
                  : economicCapacity === 'Medium'
                    ? 'amber'
                    : 'red'
              }
              size="md"
            >
              {t(`leads.enrichment.capacity${economicCapacity}`)}
            </Badge>
          </HStack>
        </>
      )}

      {/* Score Breakdown */}
      {hasScoreBreakdown && (
        <>
          <ProfileDivider />
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <BarChart3 size={14} color={colors.primary[400]} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.enrichment.scoreBreakdown')}
              </Text>
            </HStack>
            {reasonScore?.painPoints != null && (
              <ScoreBar
                label={t('leads.enrichment.painPointsScore')}
                score={reasonScore.painPoints}
                icon={
                  <AlertTriangle size={12} color={colors.status.error.main} strokeWidth={1.5} />
                }
              />
            )}
            {reasonScore?.economicCapacity != null && (
              <ScoreBar
                label={t('leads.enrichment.economicScore')}
                score={reasonScore.economicCapacity}
                icon={<DollarSign size={12} color={colors.accent.amber[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.investmentMentality != null && (
              <ScoreBar
                label={t('leads.enrichment.investmentScore')}
                score={reasonScore.investmentMentality}
                icon={<TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.urgency != null && (
              <ScoreBar
                label={t('leads.enrichment.urgencyScore')}
                score={reasonScore.urgency}
                icon={<Flame size={12} color={colors.accent.amber[500]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.brandFocus != null && (
              <ScoreBar
                label={t('leads.enrichment.brandFocusScore')}
                score={reasonScore.brandFocus}
                icon={<Sparkles size={12} color={colors.primary[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.contentQuality != null && (
              <ScoreBar
                label={t('leads.enrichment.contentQualityScore')}
                score={reasonScore.contentQuality}
                icon={<BarChart3 size={12} color={colors.accent.purple[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.digitalPresence != null && (
              <ScoreBar
                label={t('leads.enrichment.digitalPresenceScore')}
                score={reasonScore.digitalPresence}
                icon={<Users size={12} color={colors.accent.blue[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.marketPosition != null && (
              <ScoreBar
                label={t('leads.enrichment.marketPositionScore')}
                score={reasonScore.marketPosition}
                icon={<TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.contentAffinity != null && (
              <ScoreBar
                label={t('leads.enrichment.contentAffinityScore')}
                score={reasonScore.contentAffinity}
                icon={<Heart size={12} color={colors.accent.purple[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.postingActivity != null && (
              <ScoreBar
                label={t('leads.enrichment.postingActivityScore')}
                score={reasonScore.postingActivity}
                icon={<BarChart3 size={12} color={colors.primary[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.audienceReach != null && (
              <ScoreBar
                label={t('leads.enrichment.audienceReachScore')}
                score={reasonScore.audienceReach}
                icon={<Users size={12} color={colors.accent.blue[400]} strokeWidth={1.5} />}
              />
            )}
            {reasonScore?.audienceInteraction != null && (
              <ScoreBar
                label={t('leads.enrichment.audienceInteractionScore')}
                score={reasonScore.audienceInteraction}
                icon={<TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />}
              />
            )}
          </VStack>
        </>
      )}

      {/* AI Analysis */}
      {aiAnalysis && (
        <>
          <ProfileDivider />
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <Brain size={14} color={colors.accent.purple[400]} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.enrichment.aiInsight')}
              </Text>
              {isTranslating && <ActivityIndicator size={10} color={colors.text.tertiary} />}
            </HStack>
            <Box backgroundColor={colors.background.secondary} borderRadius="md" padding="sm">
              <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
                {translatedAi ?? aiAnalysis}
              </Text>
            </Box>
          </VStack>
        </>
      )}

      {/* Pain Points */}
      {painPoints && painPoints.length > 0 && (
        <>
          <ProfileDivider />
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <AlertTriangle size={14} color={colors.status.warning.main} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.enrichment.painPoints')}
              </Text>
            </HStack>
            {(translatedPain ?? painPoints).map((point, i) => (
              <HStack key={`pain-${i}`} spacing="sm" align="start">
                <Box
                  width={4}
                  height={4}
                  borderRadius="full"
                  backgroundColor={colors.status.warning.main}
                  style={{ marginTop: 6 }}
                />
                <Text
                  variant="caption"
                  color={colors.text.secondary}
                  style={{ flex: 1, lineHeight: 16 }}
                >
                  {point}
                </Text>
              </HStack>
            ))}
          </VStack>
        </>
      )}

      {/* Footer */}
      {enrichedAt && (
        <>
          <ProfileDivider />
          <HStack spacing="md" align="center" justify="between">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.enrichment.enrichedLabel')}{' '}
              {formatDistanceToNow(new Date(enrichedAt), {
                addSuffix: true,
                locale: DATE_LOCALES[language],
              })}
            </Text>
            {enrichmentMethod && (
              <Badge variant="subtle" color="neutral" size="sm">
                {t(enrichmentMethodLabelKey(enrichmentMethod))}
              </Badge>
            )}
          </HStack>
        </>
      )}

      {/* Actions */}
      <ProfileDivider />
      <HStack spacing="sm" justify="center" style={{ flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" onPress={onEdit}>
          {t('leads.edit.title')}
        </Button>
        {contact.sourceUrl && (
          <Button variant="ghost" size="sm" onPress={onRescan} loading={isRescanning}>
            {t('common.actions.refresh')}
          </Button>
        )}
        {!contact.isPrimary && (
          <Button variant="ghost" size="sm" onPress={onSetPrimary}>
            {t('common.actions.setPrimary')}
          </Button>
        )}
        {contacts.length > 1 && (
          <Button variant="ghost" size="sm" onPress={onRemove}>
            <Text variant="caption" color={colors.status.error.main}>
              {t('common.actions.remove')}
            </Text>
          </Button>
        )}
      </HStack>
    </VStack>
  );
}

function PaginationDots({
  count,
  activeIndex,
  onDotPress,
}: Readonly<{
  count: number;
  activeIndex: number;
  onDotPress: (i: number) => void;
}>) {
  return (
    <HStack spacing="xs" justify="center" style={{ paddingTop: tokens.spacing.sm }}>
      {Array.from({ length: count }).map((_, i) => (
        <Pressable key={i} onPress={() => onDotPress(i)}>
          <Box
            width={activeIndex === i ? 20 : 8}
            height={8}
            borderRadius="full"
            backgroundColor={activeIndex === i ? colors.primary.main : colors.border.default}
          />
        </Pressable>
      ))}
    </HStack>
  );
}

export function ContactCarouselCard({ leadId, contacts }: Readonly<ContactCarouselCardProps>) {
  const { t } = useTranslation();
  const { confirm } = useModal();
  const addContact = useAddContact();
  const updateContact = useUpdateContact();
  const removeContact = useRemoveContact();
  const setPrimary = useSetPrimaryContact();
  const rescanContact = useRescanContact();

  const [activeIndex, setActiveIndex] = useState(0);
  const [sheetMode, setSheetMode] = useState<'edit' | 'add' | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [editForm, setEditForm] = useState<ContactFormData>(EMPTY_FORM);
  const [addForm, setAddForm] = useState<ContactFormData>(EMPTY_FORM);

  const slideOpacity = useRef(new Animated.Value(1)).current;
  const slideTranslateX = useRef(new Animated.Value(0)).current;

  const goToIndex = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= contacts.length || idx === activeIndex) return;
      const direction = idx > activeIndex ? 1 : -1;
      Animated.timing(slideOpacity, {
        toValue: 0,
        duration: 120,
        useNativeDriver: true,
        easing: Easing.out(Easing.ease),
      }).start(() => {
        setActiveIndex(idx);
        slideTranslateX.setValue(direction * 40);
        Animated.parallel([
          Animated.timing(slideOpacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
            easing: Easing.out(Easing.cubic),
          }),
          Animated.timing(slideTranslateX, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
            easing: Easing.out(Easing.cubic),
          }),
        ]).start();
      });
    },
    [contacts.length, activeIndex, slideOpacity, slideTranslateX],
  );

  useEffect(() => {
    if (activeIndex >= contacts.length && contacts.length > 0) {
      setActiveIndex(contacts.length - 1);
    }
  }, [contacts.length, activeIndex]);

  const closeSheet = () => {
    setSheetMode(null);
    setSelectedContact(null);
    setEditForm(EMPTY_FORM);
    setAddForm(EMPTY_FORM);
  };

  const startEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setEditForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      jobTitle: contact.jobTitle || '',
      role: contact.role || '',
    });
    setSheetMode('edit');
  };

  const handleUpdate = useCallback(async () => {
    if (!selectedContact) return;
    await updateContact.mutateAsync({
      leadId,
      contactId: selectedContact.id,
      data: {
        name: clearableField(editForm.name),
        email: clearableField(editForm.email),
        phone: clearableField(editForm.phone),
        jobTitle: clearableField(editForm.jobTitle),
        role: editForm.role || null,
      },
    });
    closeSheet();
  }, [updateContact, editForm, leadId, selectedContact]);

  const handleAdd = useCallback(async () => {
    if (!addForm.name && !addForm.email && !addForm.phone) return;
    try {
      await addContact.mutateAsync({
        leadId,
        data: {
          name: addForm.name || undefined,
          email: addForm.email || undefined,
          phone: addForm.phone || undefined,
          jobTitle: addForm.jobTitle || undefined,
          role: addForm.role || null,
        },
      });
      closeSheet();
    } catch (err) {
      const message =
        (err as any)?.response?.data?.message || (err as Error)?.message || 'Failed to add contact';
      if (Platform.OS === 'web') {
        window.alert(message);
      } else {
        Alert.alert('Error', message);
      }
    }
  }, [addContact, addForm, leadId, t]);

  const handleRemove = useCallback(
    async (contact: Contact) => {
      const confirmed = await confirm({
        title: t('leads.contacts.removeTitle'),
        message:
          `${t('leads.contacts.removeMessage')} ${contact.name || ''}`.trim() +
          `. ${t('leads.contacts.removeWarning')}`,
        confirmLabel: t('common.actions.delete'),
        variant: 'destructive',
      });
      if (!confirmed) return;
      removeContact.mutate({ leadId, contactId: contact.id });
    },
    [confirm, removeContact, leadId, t],
  );

  const handleSetPrimary = useCallback(
    (contact: Contact) => {
      setPrimary.mutate({ leadId, contactId: contact.id });
    },
    [setPrimary, leadId],
  );

  const sheetTitle = sheetMode === 'add' ? 'New Contact' : t('leads.edit.title');

  return (
    <>
      <Box style={styles.card}>
        <BentoSurface radius={tokens.radius.xl} />
        <VStack spacing="sm">
          {/* Header with nav arrows */}
          <HStack justify="between" align="center">
            <HStack spacing="xs" align="center">
              {contacts.length > 1 && (
                <Pressable
                  onPress={() => goToIndex(Math.max(0, activeIndex - 1))}
                  disabled={activeIndex === 0}
                  style={{ opacity: activeIndex === 0 ? 0.3 : 1 }}
                >
                  <ChevronLeft size={20} color={colors.text.primary} strokeWidth={2} />
                </Pressable>
              )}
              <Users size={16} color={colors.primary.main} strokeWidth={2} />
              <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
                {contacts.length === 1
                  ? contacts[0].name || t('leads.contacts.singular')
                  : `${activeIndex + 1} / ${contacts.length}`}
              </Text>
              {contacts.length > 1 && (
                <Pressable
                  onPress={() => goToIndex(Math.min(contacts.length - 1, activeIndex + 1))}
                  disabled={activeIndex === contacts.length - 1}
                  style={{ opacity: activeIndex === contacts.length - 1 ? 0.3 : 1 }}
                >
                  <ChevronRight size={20} color={colors.text.primary} strokeWidth={2} />
                </Pressable>
              )}
            </HStack>
            <Pressable
              onPress={() => {
                setAddForm(EMPTY_FORM);
                setSheetMode('add');
              }}
              accessibilityRole="button"
              accessibilityLabel={t('common.actions.create')}
            >
              <Box style={styles.addButton}>
                <Plus size={16} color={colors.primary.main} strokeWidth={2} />
              </Box>
            </Pressable>
          </HStack>

          {/* Active contact */}
          {contacts[activeIndex] && (
            <Animated.View
              style={{ opacity: slideOpacity, transform: [{ translateX: slideTranslateX }] }}
            >
              <ContactSlide
                contact={contacts[activeIndex]}
                contacts={contacts}
                onEdit={() => startEdit(contacts[activeIndex])}
                onSetPrimary={() => handleSetPrimary(contacts[activeIndex])}
                onRemove={() => handleRemove(contacts[activeIndex])}
                onRescan={() => {
                  const url = contacts[activeIndex].sourceUrl;
                  if (url) rescanContact.mutate({ leadId, sourceUrl: url });
                }}
                isRescanning={rescanContact.isPending}
              />
            </Animated.View>
          )}

          {/* Dots */}
          {contacts.length > 1 && (
            <PaginationDots
              count={contacts.length}
              activeIndex={activeIndex}
              onDotPress={goToIndex}
            />
          )}
        </VStack>
      </Box>

      {/* Edit / Add Sheet */}
      <Sheet visible={sheetMode !== null} onClose={closeSheet} title={sheetTitle} size="sm">
        <ScrollView style={{ flex: 1 }}>
          {sheetMode === 'edit' && selectedContact && (
            <VStack spacing="md" style={{ padding: tokens.spacing.lg }}>
              <FormField
                label={t('leads.contacts.nameLabel')}
                value={editForm.name}
                onChangeText={(v) => setEditForm((f) => ({ ...f, name: v }))}
                placeholder={t('leads.contacts.namePlaceholder')}
              />
              <FormField
                label={t('leads.contacts.emailLabel')}
                value={editForm.email}
                onChangeText={(v) => setEditForm((f) => ({ ...f, email: v }))}
                placeholder={t('leads.contacts.emailPlaceholder')}
                keyboardType="email-address"
              />
              <FormField
                label={t('leads.contacts.phoneLabel')}
                value={editForm.phone}
                onChangeText={(v) => setEditForm((f) => ({ ...f, phone: v }))}
                placeholder={t('leads.contacts.phonePlaceholder')}
                keyboardType="phone-pad"
              />
              <FormField
                label={t('leads.contacts.jobTitleLabel')}
                value={editForm.jobTitle}
                onChangeText={(v) => setEditForm((f) => ({ ...f, jobTitle: v }))}
                placeholder={t('leads.contacts.jobTitlePlaceholder')}
              />
              <VStack spacing="xxs">
                <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                  {t('leads.contacts.roleLabel')}
                </Text>
                <Select
                  variant="dropdown"
                  value={editForm.role}
                  onChange={(v) => setEditForm((f) => ({ ...f, role: v }))}
                  placeholder={t('leads.contacts.rolePlaceholder')}
                  options={CONTACT_ROLE_OPTIONS.map((r) => ({
                    value: r,
                    label: t(`leads.contacts.roles.${r}`),
                  }))}
                />
              </VStack>
              <HStack spacing="sm" justify="end" style={{ marginTop: tokens.spacing.md }}>
                <Button variant="ghost" size="md" onPress={closeSheet}>
                  {t('common.actions.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onPress={handleUpdate}
                  loading={updateContact.isPending}
                >
                  {t('common.actions.save')}
                </Button>
              </HStack>
            </VStack>
          )}
          {sheetMode === 'add' && (
            <VStack spacing="md" style={{ padding: tokens.spacing.lg }}>
              <FormField
                label={t('leads.contacts.nameLabel')}
                value={addForm.name}
                onChangeText={(v) => setAddForm((f) => ({ ...f, name: v }))}
                placeholder={t('leads.contacts.namePlaceholder')}
              />
              <FormField
                label={t('leads.contacts.emailLabel')}
                value={addForm.email}
                onChangeText={(v) => setAddForm((f) => ({ ...f, email: v }))}
                placeholder={t('leads.contacts.emailPlaceholder')}
                keyboardType="email-address"
              />
              <FormField
                label={t('leads.contacts.phoneLabel')}
                value={addForm.phone}
                onChangeText={(v) => setAddForm((f) => ({ ...f, phone: v }))}
                placeholder={t('leads.contacts.phonePlaceholder')}
                keyboardType="phone-pad"
              />
              <FormField
                label={t('leads.contacts.jobTitleLabel')}
                value={addForm.jobTitle}
                onChangeText={(v) => setAddForm((f) => ({ ...f, jobTitle: v }))}
                placeholder={t('leads.contacts.jobTitlePlaceholder')}
              />
              <VStack spacing="xxs">
                <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                  {t('leads.contacts.roleLabel')}
                </Text>
                <Select
                  variant="dropdown"
                  value={addForm.role}
                  onChange={(v) => setAddForm((f) => ({ ...f, role: v }))}
                  placeholder={t('leads.contacts.rolePlaceholder')}
                  options={CONTACT_ROLE_OPTIONS.map((r) => ({
                    value: r,
                    label: t(`leads.contacts.roles.${r}`),
                  }))}
                />
              </VStack>
              <HStack spacing="sm" justify="end" style={{ marginTop: tokens.spacing.md }}>
                <Button variant="ghost" size="md" onPress={closeSheet}>
                  {t('common.actions.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onPress={handleAdd}
                  loading={addContact.isPending}
                  disabled={!addForm.name && !addForm.email && !addForm.phone}
                >
                  {t('common.actions.create')}
                </Button>
              </HStack>
            </VStack>
          )}
        </ScrollView>
      </Sheet>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    ...cardSurface(),
    padding: tokens.spacing.lg,
    overflow: 'hidden',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  formInput: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.md,
    minHeight: 48,
    ...Platform.select({
      web: { outlineStyle: 'none' } as unknown as object,
    }),
  },
  fieldLabel: {
    fontWeight: '500' as const,
    letterSpacing: 0.3,
  },
});
